import { useEffect, useMemo, useState } from 'react';
import SiteLayout from '@/components/feature/SiteLayout';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SoftwareCard from '@/components/feature/SoftwareCard';
import Pagination from '@/components/feature/Pagination';
import { categories } from '@/mocks/categories';
import { softwares } from '@/mocks/softwares';

const PAGE_SIZE = 6;

type SortKey = 'latest' | 'downloads' | 'rating';

const sortOptions: { key: SortKey; label: string }[] = [
  { key: 'latest', label: '最新发布' },
  { key: 'downloads', label: '热门下载' },
  { key: 'rating', label: '评分最高' },
];

export default function SoftwaresPage() {
  const [activeCategory, setActiveCategory] = useState('all');
  const [freeOnly, setFreeOnly] = useState(false);
  const [sort, setSort] = useState<SortKey>('latest');
  const [view, setView] = useState<'grid' | 'list'>('grid');
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);

  const filtered = useMemo(() => {
    let list = [...softwares];
    if (activeCategory !== 'all') {
      list = list.filter((s) => s.categoryId === activeCategory);
    }
    if (freeOnly) {
      list = list.filter((s) => s.free);
    }
    if (sort === 'latest') {
      list.sort((a, b) => (a.publishDate < b.publishDate ? 1 : -1));
    } else if (sort === 'downloads') {
      list.sort((a, b) => b.downloads - a.downloads);
    } else {
      list.sort((a, b) => b.rating - a.rating);
    }
    return list;
  }, [activeCategory, freeOnly, sort]);

  useEffect(() => {
    setLoading(true);
    setPage(1);
    const timer = setTimeout(() => setLoading(false), 260);
    return () => clearTimeout(timer);
  }, [activeCategory, freeOnly, sort]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const paged = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  const resetFilters = () => {
    setActiveCategory('all');
    setFreeOnly(false);
    setSort('latest');
  };

  return (
    <SiteLayout>
      <Breadcrumb items={[{ label: '软件下载' }]} />

      <div className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
        <h1 className="font-heading text-xl md:text-2xl font-bold text-foreground-950">
          全部软件
        </h1>
        <p className="mt-2 text-sm text-foreground-600 max-w-3xl leading-relaxed">
          汇总本站收录的黄金投资相关软件与应用，支持按功能类型、是否免费与排序方式筛选，
          快速找到行情分析、价格监控与理财工具。
        </p>
      </div>

      <div className="mt-5 rounded-lg border border-background-200 bg-background-100 p-4 md:p-5">
        <div className="flex flex-col gap-4">
          <div className="flex items-start gap-3">
            <span className="text-sm text-foreground-500 whitespace-nowrap pt-1.5 w-16 shrink-0">
              功能类型
            </span>
            <div className="flex items-center gap-2 flex-wrap">
              <button
                type="button"
                onClick={() => setActiveCategory('all')}
                className={`h-8 px-3 rounded-full text-sm whitespace-nowrap cursor-pointer transition-colors ${
                  activeCategory === 'all'
                    ? 'bg-secondary-950 text-background-50 font-medium'
                    : 'bg-background-50 text-foreground-600 border border-background-200 hover:border-primary-300'
                }`}
              >
                全部
              </button>
              {categories.map((cat) => (
                <button
                  key={cat.id}
                  type="button"
                  onClick={() => setActiveCategory(cat.id)}
                  className={`h-8 px-3 rounded-full text-sm whitespace-nowrap cursor-pointer transition-colors ${
                    activeCategory === cat.id
                      ? 'bg-secondary-950 text-background-50 font-medium'
                      : 'bg-background-50 text-foreground-600 border border-background-200 hover:border-primary-300'
                  }`}
                >
                  {cat.name}
                </button>
              ))}
            </div>
          </div>

          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
            <div className="flex items-center gap-3 flex-wrap">
              <span className="text-sm text-foreground-500 whitespace-nowrap w-16 shrink-0">
                价格
              </span>
              <button
                type="button"
                onClick={() => setFreeOnly(false)}
                className={`h-8 px-3 rounded-full text-sm whitespace-nowrap cursor-pointer transition-colors ${
                  !freeOnly
                    ? 'bg-primary-500 text-secondary-950 font-medium'
                    : 'bg-background-50 text-foreground-600 border border-background-200'
                }`}
              >
                全部
              </button>
              <button
                type="button"
                onClick={() => setFreeOnly(true)}
                className={`h-8 px-3 rounded-full text-sm whitespace-nowrap cursor-pointer transition-colors ${
                  freeOnly
                    ? 'bg-primary-500 text-secondary-950 font-medium'
                    : 'bg-background-50 text-foreground-600 border border-background-200'
                }`}
              >
                仅看免费
              </button>
            </div>

            <div className="flex items-center gap-3 flex-wrap">
              <div className="flex items-center gap-2">
                <span className="text-sm text-foreground-500 whitespace-nowrap">排序</span>
                <select
                  value={sort}
                  onChange={(e) => setSort(e.target.value as SortKey)}
                  className="h-9 px-3 rounded-md border border-background-200 bg-background-50 text-sm text-foreground-700 outline-none focus:border-primary-400 cursor-pointer"
                >
                  {sortOptions.map((opt) => (
                    <option key={opt.key} value={opt.key}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex items-center bg-background-50 border border-background-200 rounded-md p-1">
                <button
                  type="button"
                  onClick={() => setView('grid')}
                  aria-label="网格视图"
                  className={`w-8 h-8 rounded-md flex items-center justify-center cursor-pointer transition-colors ${
                    view === 'grid' ? 'bg-secondary-950 text-background-50' : 'text-foreground-500'
                  }`}
                >
                  <i className="ri-grid-line text-base" />
                </button>
                <button
                  type="button"
                  onClick={() => setView('list')}
                  aria-label="列表视图"
                  className={`w-8 h-8 rounded-md flex items-center justify-center cursor-pointer transition-colors ${
                    view === 'list' ? 'bg-secondary-950 text-background-50' : 'text-foreground-500'
                  }`}
                >
                  <i className="ri-list-check-2 text-base" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <p className="mt-4 text-sm text-foreground-500">
        共 <span className="text-primary-700 font-medium">{filtered.length}</span> 款软件
      </p>

      {loading ? (
        <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
          {[0, 1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="rounded-lg border border-background-200 bg-background-50 overflow-hidden">
              <div className="w-full h-40 bg-background-200 animate-pulse" />
              <div className="p-4 flex flex-col gap-3">
                <div className="h-4 w-2/3 bg-background-200 rounded animate-pulse" />
                <div className="h-3 w-full bg-background-100 rounded animate-pulse" />
                <div className="h-3 w-4/5 bg-background-100 rounded animate-pulse" />
                <div className="h-9 w-full bg-background-200 rounded-md animate-pulse" />
              </div>
            </div>
          ))}
        </div>
      ) : paged.length > 0 ? (
        <>
          <div
            className={
              view === 'grid'
                ? 'mt-4 grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4'
                : 'mt-4 flex flex-col gap-4'
            }
          >
            {paged.map((s) => (
              <SoftwareCard key={s.id} software={s} variant={view} />
            ))}
          </div>
          <Pagination current={safePage} total={totalPages} onChange={setPage} />
        </>
      ) : (
        <div className="mt-4 rounded-lg border border-background-200 bg-background-50 p-12 text-center">
          <span className="w-16 h-16 mx-auto rounded-full bg-background-100 flex items-center justify-center text-foreground-400">
            <i className="ri-inbox-line text-3xl" />
          </span>
          <h3 className="mt-4 font-heading text-base font-semibold text-foreground-950">
            暂无相关软件
          </h3>
          <p className="mt-2 text-sm text-foreground-500">
            当前筛选条件下没有匹配的软件，试试重置筛选。
          </p>
          <button
            type="button"
            onClick={resetFilters}
            className="mt-5 inline-flex h-10 px-5 rounded-md bg-primary-500 hover:bg-primary-600 text-secondary-950 text-sm font-medium whitespace-nowrap cursor-pointer transition-colors items-center justify-center"
          >
            重置筛选
          </button>
        </div>
      )}
    </SiteLayout>
  );
}